import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { briefApi, type BriefResponse, type BriefSignal } from '../services/briefApi';

const SEVERITY_COLOR: Record<number, string> = {
  3: 'border-red/50 bg-red/10 text-red',
  2: 'border-accent/40 bg-accent-soft text-accent-bright',
  1: 'border-border bg-bg-input/40 text-text-dim',
};

const SIGNAL_TYPE_LABEL: Record<string, string> = {
  velocity: 'velocity',
  new_entrant: 'new entrant',
  lapse_cluster: 'lapse cluster',
  pool_overlap: 'pool overlap',
  watch_list: 'watch list',
  lease_term_estimate: 'lease term',
};

function relativeDays(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return 'today';
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function signalTargetHref(s: BriefSignal): string | null {
  if (!s.scope_id) return null;
  if (s.scope === 'aoi') return `/mi/aoi/${s.scope_id}`;
  if (s.scope === 'pool_order') return `/mi/pool-orders/${s.scope_id}`;
  return null;
}

export function Brief() {
  const [data, setData] = useState<BriefResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);

  async function reload() {
    try {
      const r = await briefApi.get();
      setData(r); setErr(null);
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to load brief');
    }
  }
  useEffect(() => { reload(); }, []);

  async function runWeekly() {
    setRunMsg(null);
    setRunning(true);
    try {
      const r = await briefApi.runWeekly();
      setRunMsg(`Inserted ${r.signals_inserted} signals · status=${r.parse_status}`);
      await reload();
    } catch (e: any) {
      setRunMsg(`Failed: ${e?.message ?? e}`);
    } finally {
      setRunning(false);
    }
  }

  if (err) {
    return <div className="px-8 py-7 max-w-[1280px] mx-auto"><div className="text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div></div>;
  }
  if (!data) {
    return <div className="px-8 py-7 text-[12px] text-text-faint font-mono">loading brief…</div>;
  }

  const lwb = data.last_weekly_brief;

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto pb-20">
      <header className="flex items-end justify-between pb-6 border-b border-border gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">workspace · monday digest</div>
          <h1 className="font-display text-[34px] leading-tight">Monday <em className="text-accent not-italic">Brief</em></h1>
          <div className="text-[12px] text-text-dim mt-1.5 font-mono">
            {lwb ? (
              <>last analysis {relativeDays(lwb.created_at)} · {lwb.parse_status === 'complete' ? `${lwb.input_token_count ?? '?'}→${lwb.output_token_count ?? '?'} tokens` : <span className="text-red">parse failed</span>}</>
            ) : <>no weekly analysis yet — click <span className="text-accent">Run weekly</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {runMsg && <span className="text-[11px] text-text-dim font-mono">{runMsg}</span>}
          <button onClick={runWeekly} disabled={running}
            className="px-4 py-2 text-[13px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50">
            {running ? 'Running (~10-20s)…' : 'Run weekly →'}
          </button>
        </div>
      </header>

      {data.lead_alert_signals.length > 0 && (
        <div className="mt-6 space-y-2">
          {data.lead_alert_signals.map(s => (
            <div key={s.id} className="border-2 border-red/40 bg-red/5 rounded px-4 py-3 flex items-center gap-3">
              <span className="text-red text-[18px] leading-none">⚡</span>
              <div className="flex-1">
                <div className="text-[14px] text-text flex items-center gap-2 flex-wrap">
                  <span>{s.headline}</span>
                  {s.pipeline_pushes && s.pipeline_pushes.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border border-green/40 bg-green/10 text-green"
                      title={`This party was pushed to Close on ${s.pipeline_pushes[0].pushed_at.slice(0,10)}`}>
                      ● in pipeline (pushed {s.pipeline_pushes[0].pushed_at.slice(0,10)})
                    </span>
                  )}
                </div>
                {s.detail && <div className="text-[11px] text-text-dim font-mono mt-0.5">{s.detail}</div>}
              </div>
              {s.cta_target && (
                <Link to={s.cta_target} className="px-3 py-1.5 text-[12px] bg-red/20 text-red border border-red/40 rounded hover:bg-red/30">
                  {s.cta_label ?? 'Open'} →
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-7 grid grid-cols-7 gap-3">
        <StatCell label="docs · last 15d"  value={data.stats.docs_last_15d} />
        <StatCell label="new leases"       value={data.stats.new_leases_last_15d} />
        <StatCell label="new deeds"        value={data.stats.new_deeds_last_15d} />
        <StatCell label="new parties"      value={data.stats.new_parties_last_15d} />
        <StatCell label="pool orders"      value={data.stats.pool_orders_last_15d} />
        <StatCell label="active signals"   value={data.signals.length} />
        <StatCell label="pinned AOIs"      value={data.stats.pinned_aoi_count} />
      </div>

      <section className="mt-8">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display text-[18px]">Signal <em className="text-accent not-italic">feed</em></h2>
          <div className="text-[11px] text-text-faint font-mono">
            {data.signals.length === 0
              ? 'no active signals'
              : `${data.signals.length} active · severity desc, newest first`}
          </div>
        </div>

        {data.signals.length === 0 ? (
          <div className="bg-bg-card-soft border border-border rounded px-5 py-10 text-center">
            <div className="text-[13px] text-text-dim">No weekly analysis has populated the signal feed yet.</div>
            <div className="text-[11px] text-text-faint font-mono mt-1.5">Click <span className="text-accent">Run weekly →</span> above to generate the first pass.</div>
          </div>
        ) : (
          <div className="bg-bg-card-soft border border-border rounded overflow-hidden">
            {data.signals.map(s => {
              const href = signalTargetHref(s);
              const colorCls = SEVERITY_COLOR[s.severity] ?? SEVERITY_COLOR[1];
              return (
                <div key={s.id} className="grid grid-cols-[60px_2fr_3fr_auto] items-start gap-4 px-5 py-4 border-b border-border last:border-b-0 hover:bg-bg-hover/30">
                  <div>
                    <span className={'inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border ' + colorCls}>
                      S{s.severity}
                    </span>
                  </div>
                  <div>
                    <div className="text-[13px] text-text flex items-center gap-2 flex-wrap">
                      <span>{s.headline}</span>
                      {s.pipeline_pushes && s.pipeline_pushes.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border border-green/40 bg-green/10 text-green"
                          title={`This party was pushed to Close on ${s.pipeline_pushes[0].pushed_at.slice(0,10)}`}>
                          ● in pipeline (pushed {s.pipeline_pushes[0].pushed_at.slice(0,10)})
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-text-faint font-mono mt-1 flex gap-2">
                      <span>{SIGNAL_TYPE_LABEL[s.signal_type] ?? s.signal_type}</span>
                      {s.scope_label && <span className="text-text-dim">· {s.scope_label}</span>}
                      <span className="text-text-ghost">· {relativeDays(s.created_at)}</span>
                    </div>
                  </div>
                  <div className="text-[12px] text-text-dim leading-snug">{s.detail ?? <span className="italic">no detail</span>}</div>
                  <div>
                    {href ? (
                      <Link to={href} className="px-3 py-1.5 text-[12px] border border-accent text-accent rounded hover:bg-accent-soft whitespace-nowrap">
                        {s.cta_label ?? 'Open'} →
                      </Link>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="mt-8">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display text-[18px]">Pinned <em className="text-accent not-italic">AOIs</em></h2>
          <div className="text-[11px] text-text-faint font-mono">{data.pinned_aois.length} pinned</div>
        </div>

        {data.pinned_aois.length === 0 ? (
          <div className="bg-bg-card-soft border border-border rounded px-5 py-8 text-center">
            <div className="text-[13px] text-text-dim">No pinned AOIs yet.</div>
            <Link to="/mi/aois" className="text-[11px] text-accent font-mono hover:text-accent-bright mt-1.5 inline-block">Pin an AOI →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {data.pinned_aois.map(a => (
              <Link key={a.id} to={`/mi/aoi/${a.id}`}
                className="bg-bg-card border border-border rounded p-4 hover:border-accent/60 hover:bg-bg-card-soft transition-colors">
                <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">{a.basin ?? 'no basin'}</div>
                <div className="font-display text-[15px] text-text mt-1">{a.name}</div>
                <div className="text-[11px] text-text-dim font-mono mt-2 flex justify-between">
                  <span>{a.trs_count} {a.trs_count === 1 ? 'tract' : 'tracts'}</span>
                  <span>{a.docs_last_15d} docs (15d) · {a.open_signals} signals</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {data.lease_expiring_soon.length > 0 && (
        <section className="mt-10">
          <details className="bg-bg-card-soft border border-border rounded">
            <summary className="px-5 py-3 cursor-pointer hover:bg-bg-hover/30">
              <span className="font-display text-[15px]">Lease term estimates</span>
              <span className="ml-2 text-[11px] text-text-faint font-mono">heuristic sweep · {data.lease_expiring_soon.length} {data.lease_expiring_soon.length === 1 ? 'lease' : 'leases'} estimated to expire in 90 days</span>
            </summary>
            <div className="border-t border-border">
              {data.lease_expiring_soon.map(l => (
                <div key={l.doc_number} className="grid grid-cols-[1fr_1fr_1.5fr_100px] gap-3 px-5 py-2 border-b border-border last:border-b-0 text-[11px] font-mono">
                  <span className="text-text">{l.grantor_label ?? <span className="text-text-faint italic">unknown</span>}</span>
                  <span className="text-text-dim">{l.trs_label ?? '—'}</span>
                  <span className="text-text-faint">eff {l.effective_date} · {l.primary_term_months}mo term</span>
                  <span className="text-amber text-right">{l.estimated_expiry?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </details>
        </section>
      )}
    </div>
  );
}

function StatCell({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-bg-card border border-border rounded px-4 py-3">
      <div className="font-display text-[26px] leading-none text-accent-bright"><em className="not-italic">{value}</em></div>
      <div className="text-[10px] text-text-faint font-mono uppercase tracking-wider mt-1.5">{label}</div>
    </div>
  );
}
